/**
 * EditorialProgressModal — twin of BriefProgressModal, driven by
 * useEditorialProgress. Same design system, same tokens, same UX pattern.
 */
import { CheckCircle2, Circle, Loader2, Sparkles, Minus, X } from "lucide-react";
import { useEditorialProgress } from "@/hooks/useEditorialProgress";

const STEPS = [
  {
    key: "scan",
    label: "Scanning signal patterns",
    sub: "5 detectors: frequency spike · topic acceleration · regional momentum · pattern convergence · market entry",
  },
  {
    key: "evidence",
    label: "Loading evidence articles",
    sub: (state) => `${state.evidence.evidenceCount || 0} articles indexed as evidence`,
  },
  {
    key: "draft",
    label: "Drafting proposals",
    sub: (state) =>
      state.draft.total
        ? `Proposal ${state.draft.index} of ${state.draft.total}${state.draft.signal_type ? ` — ${state.draft.signal_type}` : ""}`
        : "Waiting for strong signals…",
  },
  {
    key: "publish",
    label: "Publishing",
    sub: "Adding to editorial layer",
  },
];

function StepIcon({ status }) {
  if (status === "completed") return <CheckCircle2 className="h-4 w-4" style={{ color: "var(--hm-accent)" }} strokeWidth={2} />;
  if (status === "running") return <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--hm-accent)" }} />;
  return (
    <Circle
      className="h-4 w-4"
      strokeWidth={1.75}
      style={{ color: "var(--hm-icon-pending)" }}
    />
  );
}

function fmtMs(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export default function EditorialProgressModal() {
  const p = useEditorialProgress();
  if (!p.isRunning && !p.endedAt) return null;
  if (p.minimized) return null;  // handled by <ProgressPillStack />

  const progressPct = Math.round(
    (Object.values(p.stepStates).filter((s) => s.status === "completed").length / STEPS.length) * 100
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-6 overflow-y-auto"
      style={{ background: "rgba(15,27,45,0.35)" }}
      data-testid="editorial-progress-modal"
    >
      <div
        className="bg-hm-bg mt-16 w-full max-w-[680px] rounded-2xl shadow-2xl overflow-hidden"
        style={{ border: "1px solid var(--hm-border)" }}
      >
        {/* Header */}
        <div className="p-6 border-b border-hm-border">
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 flex items-center justify-center rounded-xl"
              style={{ width: 44, height: 44, background: "var(--hm-accent-soft)" }}
            >
              <Sparkles className="h-5 w-5" style={{ color: "var(--hm-accent)" }} strokeWidth={2} />
            </div>
            <div className="flex-1">
              <h2 className="text-hm-fg" style={{ fontSize: 20, fontWeight: 600 }}>
                Drafting editorial proposals
              </h2>
              <p className="text-hm-fg-muted mt-1" style={{ fontSize: 13 }}>
                Detecting signals and generating story angles from your knowledge base
              </p>
            </div>
            <button
              type="button"
              onClick={p.minimize}
              className="p-1 text-hm-fg-muted hover:text-hm-fg bg-transparent border-0"
              title="Minimize"
              data-testid="editorial-modal-minimize"
            >
              <Minus className="h-4 w-4" />
            </button>
            {!p.isRunning && (
              <button
                type="button"
                onClick={p.dismiss}
                className="p-1 text-hm-fg-muted hover:text-hm-fg bg-transparent border-0"
                title="Close"
                data-testid="editorial-modal-close"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Progress bar */}
          <div
            className="mt-5 rounded-full overflow-hidden"
            style={{ height: 6, background: "var(--hm-bg-elev-2)" }}
          >
            <div
              className="h-full transition-all"
              style={{
                width: `${progressPct}%`,
                background: "var(--hm-accent)",
                transitionDuration: "500ms",
              }}
            />
          </div>
          <div className="flex justify-between mt-2 text-hm-fg-muted font-medium" style={{ fontSize: 12 }}>
            <span>{fmtMs(p.latencyMs)} elapsed</span>
            <span>{progressPct}%</span>
          </div>
        </div>

        {/* Steps */}
        <div className="p-6 space-y-4">
          {STEPS.map((s) => {
            const state = p.stepStates[s.key] || {};
            const sub = typeof s.sub === "function" ? s.sub(p.stepStates) : s.sub;
            return (
              <div key={s.key} className="flex items-start gap-3" data-testid={`editorial-step-${s.key}`}>
                <div className="mt-0.5 shrink-0"><StepIcon status={state.status} /></div>
                <div className="flex-1 min-w-0">
                  <div
                    className={
                      state.status === "running"
                        ? "text-sm text-hm-fg font-semibold"
                        : state.status === "completed"
                          ? "text-sm text-hm-fg font-medium"
                          : "text-sm text-hm-fg-muted font-medium"
                    }
                  >
                    {s.label}
                  </div>
                  {state.status !== "pending" && sub ? (
                    <div className="mono text-hm-fg-muted mt-1" style={{ fontSize: 12 }}>{sub}</div>
                  ) : null}
                  {s.key === "scan" && state.detectorsDone && state.detectorsDone.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {state.detectorsDone.map((d) => (
                        <span
                          key={d}
                          className="mono uppercase tracking-wider rounded-full"
                          style={{
                            fontSize: 10, padding: "2px 6px",
                            background: "var(--hm-accent-soft)",
                            color: "var(--hm-accent-dim)",
                          }}
                        >
                          {d}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Live feed */}
        {p.feed.length > 0 && (
          <div className="px-6 pb-3">
            <div className="text-[11px] uppercase tracking-wider text-hm-fg-muted font-semibold mb-2">
              Live feed
            </div>
            <div className="space-y-1.5" data-testid="editorial-feed">
              {p.feed.slice(0, 5).map((f, i) => (
                <div
                  key={i}
                  className="text-hm-fg truncate rounded-md px-2.5 py-1.5"
                  style={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    background: f.kind === "proposal" ? "var(--hm-accent-soft)" : "var(--hm-bg-elev-1)",
                    borderLeft: f.kind === "proposal"
                      ? "2px solid var(--hm-accent)"
                      : "2px solid var(--hm-border-strong)",
                  }}
                >
                  {f.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KPI counters */}
        <div className="grid grid-cols-4 gap-3 border-t border-hm-border p-6" data-testid="editorial-progress-counters">
          <Kpi label="Signals detected" value={p.counters.signals_detected} />
          <Kpi label="Strong signals" value={p.counters.strong_signals} />
          <Kpi label="Proposals drafted" value={p.counters.proposals_drafted} />
          <Kpi label="Duration" value={fmtMs(p.latencyMs)} />
        </div>

        {/* Footer */}
        <div
          className="px-6 py-3 flex items-center justify-between border-t border-hm-border"
          style={{ background: "var(--hm-bg-elev-3)" }}
        >
          <div className="text-hm-fg-muted font-medium" style={{ fontSize: 12 }}>
            {p.isRunning ? "Streaming live" : (p.error ? `Error · ${p.error}` : "Complete")}
          </div>
          {p.isRunning && (
            <button
              type="button"
              onClick={p.minimize}
              className="text-[12px] font-medium text-hm-accent hover:text-hm-accent-dim bg-transparent border-0 px-2 py-1 cursor-pointer"
              data-testid="editorial-progress-continue-bg"
            >
              Continue in background →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div
      className="min-w-0 rounded-md px-3 py-2.5"
      style={{ background: "var(--hm-bg-elev-3)", border: "1px solid var(--hm-border)" }}
    >
      <div className="text-[11px] uppercase tracking-wider text-hm-fg-muted font-semibold">{label}</div>
      <div className="text-hm-fg mt-1" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
    </div>
  );
}

function MinimizedPill({ p }) {
  return (
    <button
      type="button"
      onClick={p.restore}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg bg-hm-bg border cursor-pointer"
      style={{ borderColor: "var(--hm-border)" }}
      data-testid="editorial-progress-pill"
    >
      {p.isRunning ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "var(--hm-accent)" }} />
      ) : (
        <Sparkles className="h-3.5 w-3.5" style={{ color: "var(--hm-accent)" }} />
      )}
      <span className="text-hm-fg" style={{ fontSize: 12, fontWeight: 500 }}>
        Editorial · {p.counters.proposals_drafted} drafted
      </span>
    </button>
  );
}
